/**
 * The TSR OWASP Findings table, for the editor and the preview.
 *
 * Deliberately a mirror of the OWASP_FINDINGS_* constants and
 * owaspFindingsRows / owaspFindingsColumns / owaspFindingsLabels in
 * backend/utils/report-helpers.js, as riskRatingDefaults.js and
 * tableLabelDefaults.js already mirror theirs. The grouping is mirrored too,
 * not just the headings: this table's whole content is derived, so a preview
 * that grouped differently from the renderer would be describing a document
 * nobody is going to receive.
 *
 * See the backend module for why unclassified findings get a row of their own
 * and why the eight categories with nothing in them are not listed by default.
 */
import { OWASP_TOP10 } from '../../constants/owasp';

export const OWASP_FINDINGS_LABELS = {
  category: 'OWASP Category', findings: 'Findings', count: 'Count',
};

export const OWASP_FINDINGS_COLUMN_KEYS = ['category', 'findings', 'count'];
export const OWASP_FINDINGS_DEFAULT_COLUMNS = ['category', 'findings', 'count'];

export const OWASP_FINDINGS_UNCLASSIFIED = 'Not yet classified';
export const OWASP_FINDINGS_NONE = 'No findings recorded';
export const OWASP_FINDINGS_EMPTY = 'No findings matched this section.';
export const OWASP_FINDINGS_SEPARATOR = ', ';

const OWASP_CODES = OWASP_TOP10.map(o => o.code);

/**
 * The category cell's text: 'A03:2021 Injection'.
 *
 * NOT owaspLabel from constants/owasp.js, which joins with an em dash. That
 * one is a dropdown label, where the dash separates a code from its name in a
 * list of options; this one is a table cell, and the renderers print a space.
 * One of them has to be the odd one out and it is better that it is the
 * picker, which nobody is comparing against a Word document.
 */
export function owaspRowLabel(code) {
  const hit = OWASP_TOP10.find(o => o.code === code);
  return hit ? `${hit.code} ${hit.name}` : '';
}

export function resolveColumnLabels(defaults, given) {
  const labels = { ...defaults };
  const src = (given && typeof given === 'object') ? given : {};
  Object.keys(labels).forEach(key => {
    if (typeof src[key] === 'string') labels[key] = src[key];
  });
  return labels;
}

export function owaspFindingsLabels(config) {
  return resolveColumnLabels(OWASP_FINDINGS_LABELS, (config || {}).columnLabels);
}

export function owaspFindingsColumns(config) {
  const given = (config || {}).columns;
  const picked = Array.isArray(given) && given.length
    ? OWASP_FINDINGS_COLUMN_KEYS.filter(k => given.includes(k))
    : [];
  return picked.length ? picked : [...OWASP_FINDINGS_DEFAULT_COLUMNS];
}

function owaspFindingName(finding, field) {
  const f = finding || {};
  const uid = String(f.finding_uid || '').trim();
  const title = String(f.title || '').trim();
  if (field === 'title') return title || uid;
  return uid || title;
}

/**
 * Natural order by identifier, so a cell listing several findings reads
 * '...-9, ...-10, ...-11' rather than in the order the findings arrived.
 *
 * Hand-rolled rather than localeCompare(..., { numeric: true }) for the same
 * reason the backend's copy is: the two have to agree exactly.
 */
function compareFindingUid(a, b) {
  const split = (f) => String((f && f.finding_uid) || '').match(/\d+|\D+/g) || [];
  const ax = split(a);
  const bx = split(b);
  for (let i = 0; i < Math.max(ax.length, bx.length); i += 1) {
    const x = ax[i];
    const y = bx[i];
    if (x === undefined) return -1;
    if (y === undefined) return 1;
    if (/^\d/.test(x) && /^\d/.test(y)) {
      const d = Number(x) - Number(y);
      if (d) return d;
    } else if (x !== y) {
      return x < y ? -1 : 1;
    }
  }
  return 0;
}

/** Normalises a stored value to a Top 10 code, or null for "not classified". */
function normaliseOwasp(value) {
  if (value === undefined || value === null) return null;
  const v = String(value).trim();
  if (!v) return null;
  return OWASP_CODES.includes(v) ? v : null;
}

/** The same rows the renderers build, in the same order. */
export function owaspFindingsRows(findings, config) {
  const cfg = config || {};
  const rows = Array.isArray(findings) ? findings : [];

  const filter = Array.isArray(cfg.severityFilter) ? cfg.severityFilter : [];
  const kept = filter.length ? rows.filter(f => filter.includes(f && f.severity)) : rows;

  const field = cfg.findingField === 'title' ? 'title' : 'uid';
  const separator = typeof cfg.separator === 'string' ? cfg.separator : OWASP_FINDINGS_SEPARATOR;
  const noneText = typeof cfg.noneText === 'string' ? cfg.noneText : OWASP_FINDINGS_NONE;

  const byCode = new Map();
  const unclassified = [];
  kept.forEach(f => {
    const code = normaliseOwasp(f && f.owasp_category);
    if (!code) { unclassified.push(f); return; }
    if (!byCode.has(code)) byCode.set(code, []);
    byCode.get(code).push(f);
  });

  const showAll = cfg.showAllCategories === true;
  const out = [];
  OWASP_CODES.forEach(code => {
    const hits = byCode.get(code) || [];
    if (!hits.length && !showAll) return;
    const names = hits.slice().sort(compareFindingUid).map(f => owaspFindingName(f, field)).filter(Boolean);
    out.push({
      code,
      category: owaspRowLabel(code),
      findings: names.length ? names.join(separator) : noneText,
      uids: names,
      count: hits.length,
      unclassified: false,
    });
  });

  if (unclassified.length && cfg.showUnclassified !== false) {
    const names = unclassified.slice().sort(compareFindingUid).map(f => owaspFindingName(f, field)).filter(Boolean);
    out.push({
      code: null,
      category: typeof cfg.unclassifiedText === 'string' ? cfg.unclassifiedText : OWASP_FINDINGS_UNCLASSIFIED,
      findings: names.join(separator),
      uids: names,
      count: unclassified.length,
      unclassified: true,
    });
  }

  return out;
}

export function owaspFindingsConfig(findings, config) {
  const cfg = config || {};
  return {
    labels: owaspFindingsLabels(cfg),
    columns: owaspFindingsColumns(cfg),
    rows: owaspFindingsRows(findings, cfg),
    emptyText: typeof cfg.emptyText === 'string' ? cfg.emptyText : OWASP_FINDINGS_EMPTY,
  };
}
